import { Link, useLocation } from '@tanstack/react-router';
import { cn } from '@/shared/lib/utils';
import { useDogStore } from '@/shared/stores/dog-store';
import { useThemeStore } from '@/shared/stores/theme-store';

// Custom Icons - with filled and outlined versions
const HomeIcon = ({ filled = false }: { filled?: boolean }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {filled ? (
      <path
        d="M12 3L4 9V21H9V15H15V21H20V9L12 3Z"
        fill="currentColor"
      />
    ) : (
      <path
        d="M12 3L4 9V21H9V15H15V21H20V9L12 3Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  </svg>
);

const TrendIcon = ({ filled = false }: { filled?: boolean }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {filled ? (
      <>
        {/* Upward trend line with fill */}
        <path
          d="M3 20L9 14L13 18L21 10V14L13 22L9 18L3 24V20Z"
          fill="currentColor"
          opacity="0.3"
        />
        <path
          d="M3 18L9 12L13 16L21 8"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Arrow head */}
        <path
          d="M17 8H21V12"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ) : (
      <>
        {/* Upward trend line */}
        <path
          d="M3 18L9 12L13 16L21 8"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Arrow head */}
        <path
          d="M17 8H21V12"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    )}
  </svg>
);

const HeartIcon = ({ filled = false }: { filled?: boolean }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WalletIcon = ({ filled = false }: { filled?: boolean }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {filled ? (
      <>
        <rect x="2" y="6" width="20" height="14" rx="2" fill="currentColor" />
        <path d="M6 6V5C6 3.89543 6.89543 3 8 3H18C19.1046 3 20 3.89543 20 5V6" stroke="currentColor" strokeWidth="2" />
        <circle cx="17" cy="13" r="1.5" fill="white" />
      </>
    ) : (
      <>
        <rect x="2" y="6" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="M6 6V5C6 3.89543 6.89543 3 8 3H18C19.1046 3 20 3.89543 20 5V6" stroke="currentColor" strokeWidth="2" />
        <circle cx="17" cy="13" r="1.5" fill="currentColor" />
      </>
    )}
  </svg>
);

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  activeIcon: React.ReactNode;
  label: string;
  isActive: boolean;
  themeColor: string;
}

function NavItem({ to, icon, activeIcon, label, isActive, themeColor }: NavItemProps) {
  return (
    <Link
      to={to}
      className={cn(
        'flex flex-col items-center justify-center gap-0.5 min-w-[64px] py-2',
        'transition-all duration-200',
        'active:scale-95',
        !isActive && 'text-gray-400 hover:text-gray-500'
      )}
      style={isActive ? { color: themeColor } : undefined}
    >
      <div
        className={cn(
          'relative p-1.5 rounded-2xl transition-all duration-200'
        )}
        style={isActive ? { backgroundColor: `${themeColor}15` } : undefined}
      >
        {isActive ? activeIcon : icon}
      </div>
      <span
        className={cn(
          'text-[10px] font-semibold',
          !isActive && 'text-gray-400'
        )}
        style={isActive ? { color: themeColor } : undefined}
      >
        {label}
      </span>
    </Link>
  );
}

export function BottomNavigation() {
  const location = useLocation();
  const selectedDogId = useDogStore((state) => state.selectedDogId);
  const getDogTheme = useThemeStore((state) => state.getDogTheme);

  // Get the current dog's theme
  const theme = getDogTheme(selectedDogId ?? '');

  const isActive = (path: string) => {
    // Home (COTIDIANO) is active for:
    // - Exact match: /
    // - All registration pages: /daily/*/register
    // - All history pages from registrations: /daily/*/history (except reports index)
    if (path === '/') {
      const isHomePage = location.pathname === '/';
      const isRegisterPage = /^\/daily\/[^/]+\/register/.test(location.pathname);
      const isHistoryPage = /^\/daily\/[^/]+\/history/.test(location.pathname);
      return isHomePage || isRegisterPage || isHistoryPage;
    }

    // RELATÓRIOS is only active for /daily index (not register/history pages)
    if (path === '/daily') {
      return location.pathname === '/daily' || location.pathname === '/daily/';
    }

    // Other paths use default startsWith logic
    return location.pathname.startsWith(path);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50">
      {/* Background with blur */}
      <div className="absolute inset-0 bg-white/98 backdrop-blur-xl border-t border-gray-100/80 shadow-[0_-2px_20px_rgba(0,0,0,0.06)]" />

      {/* Content */}
      <div className="relative max-w-md mx-auto px-2 pb-safe">
        <div className="flex items-end justify-around py-1.5">
          <NavItem
            to="/"
            icon={<HomeIcon />}
            activeIcon={<HomeIcon filled />}
            label="COTIDIANO"
            isActive={isActive('/')}
            themeColor={theme.preview}
          />

<NavItem
            to="/health"
            icon={<HeartIcon />}
            activeIcon={<HeartIcon filled />}
            label="SAÚDE"
            isActive={isActive('/health')}
            themeColor={theme.preview}
          />

          <NavItem
            to="/daily"
            icon={<TrendIcon />}
            activeIcon={<TrendIcon filled />}
            label="RELATÓRIOS"
            isActive={isActive('/daily')}
            themeColor={theme.preview}
          />


          <NavItem
            to="/expenses"
            icon={<WalletIcon />}
            activeIcon={<WalletIcon filled />}
            label="CARTEIRA"
            isActive={isActive('/expenses')}
            themeColor={theme.preview}
          />
        </div>
      </div>
    </div>
  );
}
